import { useMemo, useState } from "react";
import { loadStripe, type StripeElementsOptionsClientSecret } from "@stripe/stripe-js";
import { Elements, PaymentElement, useElements, useStripe } from "@stripe/react-stripe-js";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";
import { useToast } from "./ui/use-toast";
import { useTranslation } from "react-i18next";

type StripePaymentModalProps = {
  open: boolean;
  clientSecret: string;
  amount: number;
  currency: string;
  metadata?: string | null;
  onClose: () => void;
  onSuccess: (paymentIntentId: string) => Promise<void>;
};

const StripePaymentForm = ({
  amount,
  currency,
  onSuccess,
}: {
  amount: number;
  currency: string;
  onSuccess: (paymentIntentId: string) => Promise<void>;
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const { toast } = useToast();
  const { t } = useTranslation();
  const [submitting, setSubmitting] = useState(false);
  const [billingDetails, setBillingDetails] = useState({
    name: "",
    email: "",
    phone: "",
    line1: "",
    city: "",
    postalCode: "",
    country: "",
  });

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!stripe || !elements) return;

    setSubmitting(true);
    const result = await stripe.confirmPayment({
      elements,
      confirmParams: {
        return_url: window.location.href,
        payment_method_data: {
          billing_details: {
            name: billingDetails.name || undefined,
            email: billingDetails.email || undefined,
            phone: billingDetails.phone || undefined,
            address: {
              line1: billingDetails.line1 || undefined,
              city: billingDetails.city || undefined,
              postal_code: billingDetails.postalCode || undefined,
              country: billingDetails.country || undefined,
            },
          },
        },
      },
      redirect: "if_required",
    });

    if (result.error) {
      toast({
        title: t("accounts.paymentConfirmErrorDescription"),
        description: result.error.message ?? "Erreur Stripe",
        variant: "destructive",
      });
      setSubmitting(false);
      return;
    }

    const intent = result.paymentIntent;
    if (intent?.id) {
      try {
        await onSuccess(intent.id);
      } finally {
        setSubmitting(false);
      }
    } else {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="flex min-h-[60vh] flex-col">
      <div className="flex-1 space-y-4 pb-20">
        <div className="rounded-xl border border-white/10 bg-white/5 p-4">
          <PaymentElement options={{ layout: "tabs" }} />
        </div>
        
      </div>
      <div className="sticky bottom-0 z-10 border-t border-white/10 bg-slate-950/95 pt-4">
        <Button
          type="submit"
          disabled={!stripe || submitting}
          className="w-full bg-gradient-to-r from-cyan-500 to-blue-500 text-white hover:from-cyan-600 hover:to-blue-600">
          {submitting
            ? t("accounts.processing", "Traitement...")
            : t("accounts.confirmPaymentButton", "Confirmer le paiement")}
        </Button>
      </div>
    </form>
  );
};

const StripePaymentModal = ({
  open,
  clientSecret,
  amount,
  currency,
  metadata,
  onClose,
  onSuccess,
}: StripePaymentModalProps) => {
  const { t } = useTranslation();
  const paymentsMode = process.env.REACT_APP_PAYMENTS_MODE ?? "test";
  const publishableKey =
    paymentsMode === "live"
      ? process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY
      : process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY_TEST;

  const stripePromise = useMemo(
    () => (publishableKey ? loadStripe(publishableKey) : null),
    [publishableKey]
  );

  if (!stripePromise) {
    return null;
  }

  const elementsOptions: StripeElementsOptionsClientSecret = {
    clientSecret,
    appearance: {
      theme: "night",
      variables: {
        colorPrimary: "#22d3ee",
        colorBackground: "#0b1220",
        colorText: "#e2e8f0",
        colorTextSecondary: "#94a3b8",
        colorDanger: "#f87171",
        spacingUnit: "6px",
        borderRadius: "12px",
        fontFamily: "Inter, system-ui, sans-serif",
      },
      rules: {
        ".Input": {
          backgroundColor: "#0f172a",
          border: "1px solid #1f2937",
        },
        ".Input:focus": {
          border: "1px solid #22d3ee",
          boxShadow: "0 0 0 2px rgba(34,211,238,0.25)",
        },
        ".Label": {
          color: "#cbd5f5",
        },
        ".Tab": {
          backgroundColor: "#0f172a",
          border: "1px solid #1f2937",
        },
        ".Tab--selected": {
          border: "1px solid #22d3ee",
          boxShadow: "0 0 0 2px rgba(34,211,238,0.15)",
        },
      },
    },
  };

  let feeRate = 0.029;
  let feeFixed = 0.3;
  let grossAmount = (amount + feeFixed) / (1 - feeRate);
  let feeAmount = grossAmount - amount;
  if (metadata) {
    try {
      const parsed = JSON.parse(metadata);
      if (typeof parsed.stripeFeeRate === "number") {
        feeRate = parsed.stripeFeeRate;
      }
      if (typeof parsed.stripeFeeFixed === "number") {
        feeFixed = parsed.stripeFeeFixed;
      }
      if (typeof parsed.stripeGrossAmount === "number") {
        grossAmount = parsed.stripeGrossAmount;
        feeAmount = grossAmount - amount;
      } else if (typeof parsed.stripeFeeEstimated === "number") {
        feeAmount = parsed.stripeFeeEstimated;
        grossAmount = amount + feeAmount;
      } else {
        grossAmount = (amount + feeFixed) / (1 - feeRate);
        feeAmount = grossAmount - amount;
      }
    } catch {
      // fallback sur le calcul local
    }
  }

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden bg-slate-950/95 text-white rounded-3xl border border-white/10">
        {/* <DialogHeader className="space-y-1">
          <DialogTitle className="text-2xl font-semibold">
            {t("accounts.paymentInitTitle", "Paiement initié")}
          </DialogTitle>
        </DialogHeader> */}
        <div className="grid max-h-[75vh] grid-cols-1 gap-6 overflow-auto pr-1 lg:grid-cols-[1.2fr_0.5fr]">
          <div className="min-w-0">
            <Elements stripe={stripePromise} options={elementsOptions}>
              <StripePaymentForm
                amount={amount}
                currency={currency}
                onSuccess={onSuccess}
              />
            </Elements>
          </div>
          <aside className="rounded-2xl border border-white/10 bg-slate-900/60 p-5">
            <div className="text-base font-semibold text-white mb-3">
              {t("payment.form.orderSummary", "Récapitulatif de la commande")}
            </div>
            <div className="flex items-center justify-between text-xs text-white/70">
              <span>
                {t("accounts.depositModalTitle", "Recharger le compte principal")}
              </span>
              <span>
                {amount.toFixed(2)} {currency}
              </span>
            </div>
            <div className="mt-2 flex items-center justify-between text-[11px] text-white/40">
              <span>{t("payment.form.vat", "T.V.A. 0%")}</span>
              <span>0.00</span>
            </div>
            <div className="mt-2 flex items-center justify-between text-[11px] text-white/50">
              <span>{t("payment.form.feesLabel", "Frais Stripe estimés")}</span>
              <span>
                {feeAmount.toFixed(2)} {currency}
              </span>
            </div>
            <div className="mt-2 flex items-center justify-between text-[11px] text-white/50">
              <span>{t("payment.form.netLabel", "Montant net")}</span>
              <span>
                {amount.toFixed(2)} {currency}
              </span>
            </div>
            <div className="mt-4 flex items-center justify-between border-t border-white/10 pt-3 text-white">
              <span className="text-sm font-semibold">
                {t("payment.form.grossLabel", "Total à payer")}
              </span>
              <span className="text-base font-semibold text-cyan-300">
                {grossAmount.toFixed(2)} {currency}
              </span>
            </div>
          </aside>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default StripePaymentModal;
